import { useState, useCallback } from 'react';
import { message } from 'antd';
import { contentService } from '../../../../services/contentService';
import { useApi } from '../../../../hooks/useApi';
import type { ContentDetailData } from '../../utils/contentDetailTypes';
import { transformApiContentToDetailData, transformDetailDataToApiContent } from '../../utils/contentDetailTransformers';

export interface UseContentDetailReturn {
  // Data
  contentDetail: ContentDetailData | null;
  metadata: {
    key: string;
    status: 'draft' | 'published' | 'archived' | 'deleted' | null;
    edit_status: 'draft' | 'published' | null;
    title: string;
    updatedAt: string;
    version: string;
    content_owner: string;
    content_members: string[];
  } | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  
  // Actions
  loadContent: (SK: string, version?: string) => Promise<boolean>;
  saveContent: (includePermissions?: boolean | { owner?: boolean; members?: boolean }) => Promise<boolean>;
  deleteContent: (SK: string) => Promise<boolean>;
  publishContent: (SK: string) => Promise<string>;
  unpublishContent: (SK: string) => Promise<boolean>;
  republishContent: (SK: string, version: string) => Promise<boolean>;
  createEditableVersion: (SK: string, version: string) => Promise<boolean>;
  updateContentDetail: (newData: ContentDetailData) => void;
  clearError: () => void;

  messageContext: React.ReactNode;
}

export const useContentDetail = (id?: string): UseContentDetailReturn => {
  const [contentDetail, setContentDetail] = useState<ContentDetailData | null>(null);
  const [metadata, setMetadata] = useState<{
    key: string;
    status: 'draft' | 'published' | 'archived' | 'deleted' | null;
    edit_status: 'draft' | 'published' | null;
    title: string;
    updatedAt: string;
    version: string;
    content_owner: string;
    content_members: string[];
  } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [messageApi, messageContext] = message.useMessage();
  
  const api = useApi();

  // Load content by SK
  const loadContent = useCallback(async (SK: string, version?: string): Promise<boolean> => {
    try {
      let response;
      
      if (version) {
        // Load specific version
        response = await api.execute(() => contentService.getContentByVersion(SK, version));
      } else {
        // Load latest version
        response = await api.execute(() => contentService.getContentBySK(SK));
      }
      
      if (response) {
        const transformedData = transformApiContentToDetailData(response);
        setContentDetail(transformedData);
        
        // Set metadata from API response
        setMetadata({
          key: response.dx_number || '',
          status: response.status,
          edit_status: response.edit_status || null,
          title: response.title_th || response.title_en || '',
          updatedAt: response.updated_at || '',
          version: response.version_number || version || '1',
          content_owner: response.content_owner || '',
          content_members: response.content_members || []
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to load content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    }
  }, [api]);

  // Save content (create or update)
  const saveContent = useCallback(async (includePermissions: boolean | { owner?: boolean; members?: boolean } = false): Promise<boolean> => {
    if (!contentDetail) {
      messageApi.error('ไม่มีเนื้อหาให้บันทึก');
      return false;
    }

    setIsSaving(true);
    try {
      const apiData = transformDetailDataToApiContent(contentDetail);
      apiData.status = 'draft'; // Ensure status is draft when saving
      
      // Handle permission inclusion based on the parameter
      if (typeof includePermissions === 'boolean') {
        // Legacy behavior: remove all permission fields if false
        if (!includePermissions) {
          delete apiData.content_owner;
          delete apiData.content_members;
        }
      } else {
        // Granular control: remove specific permission fields
        if (!includePermissions.owner) {
          delete apiData.content_owner;
        }
        if (!includePermissions.members) {
          delete apiData.content_members;
        }
      }
      
      let response;
      
      if (id) {
        // Update existing content
        response = await api.execute(() => contentService.updateContent(id, apiData));
        if (response === null)
        {
          messageApi.error('ไม่สามารถบันทึกเนื้อหาได้ กรุณาลองใหม่อีกครั้ง');
          return false;
        }
        messageApi.success(`บันทึกเนื้อหาสำเร็จ`);
      }

      if (response && response.content) {
        const updatedData = transformApiContentToDetailData(response.content);
        setContentDetail(updatedData);
      }
      return true;
    } catch (error) {
      console.error('❌ Failed to save content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [contentDetail, id, api]);

  // Delete content
  const deleteContent = useCallback(async (SK: string): Promise<boolean> => {
    setIsSaving(true);
    try {
      await api.execute(() => contentService.deleteContent(SK));
      messageApi.success('ลบรายการสำเร็จ');
      return true;
    } catch (error) {
      console.error('❌ Failed to delete content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [api]);

  // Publish content
  const publishContent = useCallback(async (SK: string): Promise<string> => {
    setIsSaving(true);
    try {
      // Save content first before publishing
      const saved = await saveContent(false);
      if (!saved) {
        return '';
      }

      const response = await api.execute(() => contentService.publishContent(SK));
      messageApi.success('เผยแพร่สำเร็จ');
      return response?.published_version || '';
    } catch (error) {
      console.error('❌ Failed to publish content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return '';
    } finally {
      setIsSaving(false);
    }
  }, [api, saveContent]);

  // Unpublish content
  const unpublishContent = useCallback(async (SK: string): Promise<boolean> => {
    setIsSaving(true);
    try {
      await api.execute(() => contentService.unpublishContent(SK));
      messageApi.success('ถอนเผยแพร่สำเร็จ');
      return true;
    } catch (error) {
      console.error('❌ Failed to unpublish content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [api]);

  // Republish content
  const republishContent = useCallback(async (SK: string, version: string): Promise<boolean> => {
    setIsSaving(true);
    try {
      await api.execute(() => contentService.republishContent(SK, version));
      messageApi.success('เผยแพร่ใหม่สำเร็จ');
      return true;
    } catch (error) {
      console.error('❌ Failed to republish content:', error);
      messageApi.error('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [api]);

  // Update content detail
  const updateContentDetail = useCallback((newData: ContentDetailData) => {
    setContentDetail(newData);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    api.clearError();
  }, [api]);

  // Create editable version from a specific version
  const createEditableVersion = useCallback(async (SK: string, version: string): Promise<boolean> => {
    try {
      const response = await api.execute(() => contentService.createEditableVersion(SK, version));
      if (response) {
        messageApi.success('สร้างเวอร์ชันแก้ไขสำเร็จ');
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to create editable version:', error);
      messageApi.error('เกิดข้อผิดพลาดในการสร้างเวอร์ชันแก้ไข กรุณาลองใหม่อีกครั้ง');
      return false;
    }
  }, [api, messageApi]);

  return {
    contentDetail: contentDetail || {} as ContentDetailData,
    metadata,
    isLoading: api.isLoading,
    isSaving,
    error: api.error,
    loadContent,
    saveContent,
    deleteContent,
    updateContentDetail,
    publishContent,
    unpublishContent,
    republishContent,
    createEditableVersion,
    clearError,
    messageContext,
  };
};